import { Inject, Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { StickyTab } from '@frk/eds-components';
import { WINDOW } from '@ng-web-apis/common';
import { WindowScrollService } from '@shared/services/window-scroll.service';
import { Logger } from '@utils/logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Logger
 */
const logger = Logger.getLogger('TabsStickyService');

@Injectable({
  providedIn: 'root',
})
export class TabsStickyService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  private sectionNavs: StickyTab[];

  /**
   * Active Tab Id
   */
  private activeTabId$: BehaviorSubject<string> = new BehaviorSubject('');

  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private scrollService: WindowScrollService,
    private activeRoute: ActivatedRoute
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Get observable of activetabId
   */
  public getActiveTabId$(): Observable<any> {
    return this.activeTabId$.asObservable();
  }

  /**
   * Set active tab id
   * @param id - Id of active tab
   */
  public scrollToTab(id: string): void {
    this.scrollService.suppressScrollSpy();
    this.activeTabId$.next(id);
  }

  /**
   * Set Section Nav List
   * @param sectionNavs object
   */
  public setSectionNavs(sectionNavs: StickyTab[]): void {
    this.sectionNavs = sectionNavs;
    this.setScrollListener();
  }

  /**
   * Listen to see when a StickyTab id comes into view,
   */
  private setScrollListener() {
    // set up ScrollSpy functionality
    const tabIds: string[] = this.sectionNavs.map(
      (tab: StickyTab): string => tab.id
    );

    // By default, check for fragment, if any, in the url.
    // If yes, then set it as active tab id.
    // Else, make first element of tabIds as active one.
    // As, below scroll service will fire only on scroll behaviour.
    this.activeRoute.fragment
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((fragment) => {
        logger.debug('new #fragment', fragment, tabIds);
        if (fragment && tabIds?.includes(fragment)) {
          this.activeTabId$.next(fragment);
        } else {
          this.activeTabId$.next(tabIds[0] || '');
        }
      });

    this.scrollService
      .getCurrentSection$(tabIds)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((id: string): void => {
        // Get current section user is viewing
        logger.debug('getCurrentSection$()', id);
        // Update the current tab id
        this.activeTabId$.next(id);
      });
  }
}
